import { Vue, Component, Prop } from "vue-property-decorator";
import { BFormGroup, BFormInput, BFormTextarea, BInputGroupAppend, BButton } from "bootstrap-vue";
import { IEmployee, ISale, ISaleStage, ITimelineStep, TimelineStepType } from "@lib";
import { datetime } from "@/filters/datetime";
import { AltTimeline, AltTimelineItem } from "@/core/alt-ui";
import * as filters from "@/filters";

@Component({
    components: {
        AltTimeline,
        AltTimelineItem,
        BFormGroup,
        BFormTextarea,
        BFormInput,
        BButton,
        BInputGroupAppend,
    },
})
export default class SaleViewTabsTimeline extends Vue {
    @Prop({ required: true })
    private sale!: ISale;

    @Prop({ type: Array, default: () => {} })
    private stages!: ISaleStage[];

    @Prop({ type: Array, default: () => [] })
    private employees!: IEmployee[];

    @Prop({ type: Boolean, default: false })
    private readonly!: boolean;

    private comment: string = "";

    private get timeline(): any[] {
        if (!this.sale) {
            return [];
        }

        let timeline = this.sale.timeline ? this.sale.timeline : [];

        timeline = timeline.sort((a, b) => {
            const dt1 = new Date(a.datetime);
            const dt2 = new Date(b.datetime);
            return dt1 > dt2 ? -1 : dt1 < dt2 ? 1 : 0;
        });

        return timeline.map(step => this.convertTimelineStep(step));
    }

    private convertTimelineStep(step: ITimelineStep): any {
        const id = step.id ? step.id : new Date(step.datetime).getTime();
        const time = datetime(step.datetime);
        const author = this.getEmployeeName(step.author as string);

        switch (step.type) {
            case TimelineStepType.Create:
                return {
                    id: id,
                    color: "success",
                    icon: "PlusIcon",
                    title: `${author} создал(а) продажу`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.Done:
                return {
                    id: id,
                    color: "success",
                    icon: "FlagIcon",
                    title: `${author} закрыл(а) продажу`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.Reopen:
                return {
                    id: id,
                    color: "success",
                    icon: "ArrowUpIcon",
                    title: `${author} снова откыл(а) продажу`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.Comment:
                return {
                    id: id,
                    color: "primary",
                    icon: "MessageSquareIcon",
                    title: `${author} оставил(а) комментарий`,
                    desc: step.text,
                    time: time,
                };
            case TimelineStepType.NotificationSms:
                return {
                    id: id,
                    color: "primary",
                    icon: "SendIcon",
                    title: `${author} отправил(а) смс`,
                    desc: step.text,
                    time: time,
                };
            case TimelineStepType.GoodCreate:
            case TimelineStepType.GoodFromStore:
                return {
                    id: id,
                    color: "success",
                    icon: "ToolIcon",
                    title: `${author} добавил(а) товар "${step.text}"`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.GoodUpdate:
            case TimelineStepType.GoodUpdateMany:
                return {
                    id: id,
                    color: "warning",
                    icon: "ToolIcon",
                    title: step.text ? `${author} изменил(а) товар "${step.text}"` : `${author} изменил(а) товар`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.GoodDelete:
            case TimelineStepType.GoodDeleteMany:
                return {
                    id: id,
                    color: "danger",
                    icon: "ToolIcon",
                    title: `${author} удалил(а) товар "${step.text}"`,
                    //desc  : "",
                    time: time,
                };

            case TimelineStepType.NumberChanged:
                return {
                    id: id,
                    color: "warning",
                    icon: "InfoIcon",
                    title: `${author} изменил(а) номер продажи на ${step.text}`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.InfoChanged:
                return {
                    id: id,
                    color: "warning",
                    icon: "InfoIcon",
                    title: `${author} изменил(а) данные продажи`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.StageChanged:
                return {
                    id: id,
                    color: "success",
                    icon: "CheckCircleIcon",
                    title: `${author} изменил(а) этап на "${this.getStageName(step.text)}"`,
                    desc: step.description,
                    time: time,
                };
            case TimelineStepType.ClientChanged:
                return {
                    id: id,
                    color: "warning",
                    icon: "UserIcon",
                    title: `${author} изменил(а) клиента`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.SellerChanged:
                return {
                    id: id,
                    color: "success",
                    icon: "UserIcon",
                    title: `${author} изменил(а) продавца на ${this.getEmployeeName(step.text)}`,
                    //desc  : "",
                    time: time,
                };
            case TimelineStepType.PaymentCreate:
                return {
                    id: id,
                    color: "success",
                    icon: "DollarSignIcon",
                    title: `${author} принял(а) средства`,
                    desc: filters.money.money2(step.text, true),
                    time: time,
                };
            case TimelineStepType.PaymentDelete:
                return {
                    id: id,
                    color: "danger",
                    icon: "DollarSignIcon",
                    title: `${author} удалил(а) платёж "${step.description}"`,
                    desc: filters.money.money2(step.text, true),
                    time: time,
                };
        }

        return {
            id: id,
            color: "primary",
            icon: "InfoIcon",
            title: step.text,
            //desc  : "",
            time: time,
        };
    }

    private getEmployeeName(id: string): string {
        if (!this.employees) {
            return id;
        }
        const employee = this.employees.find((emp: any) => emp.user === id);
        return employee ? employee.userRef.info.name : id;
    }

    private getStageName(id: string): string {
        if (!this.stages) {
            return id;
        }
        const stage = this.stages.find((s: any) => s.id === id);
        return stage ? stage.name : id;
    }

    private async addComment(): Promise<void> {
        try {
            if (this.comment.length === 0) {
                return;
            }

            const companyId = this.sale.company;
            const saleId = this.sale.id;
            const comment = this.comment;

            const usecase = this.$alt.system.usecase.createSaleUseCase();
            const sale = await usecase.addComment(companyId, saleId, comment);

            this.comment = "";
            this.$emit("sale-changed", sale);
        } catch {
            this.$alt.toast.error("Не удалось добавить комментарий.");
        }
    }
}
